<script lang="ts">
  import {
    applyReducer,
    getFrameStyle,
    styleToString,
  } from "@shared/common/node/style";
  import type {
    IFrameNode,
    IMediaPaint,
    PartialNode,
  } from "@shared/common/node/type";
  import { getAspectRatioStyle } from "@shared/common/node/util";
  import type { VideoInfo } from "@shared/common/types/data-source";
  import { getDataSourceValue } from "@shared/common/utils/data-source";
  import { assertCondition, isObject } from "@shared/common/utils/value";
  import { onMount } from "svelte";
  import detectReaction from "../../actions/detect-reaction";
  import { getCurrentDocumentNode, getStore } from "../../context";
  import pubsub from "../../pubsub";
  import BackgroundVideo from "./background-video.svelte";

  const { reactionEventListener, dataMap } = getStore();

  const documentNode = getCurrentDocumentNode();

  export let node: PartialNode<IFrameNode>;
  // 캡처시 가장 최상단 프레임의 그림자를 제거
  export let hideShadow: boolean;

  let show = false;
  let image: IMediaPaint | undefined = node.fills?.find(
    (fill): fill is IMediaPaint => fill.type === "IMAGE"
  );
  let isBackgroundVideo =
    node.fills && image?.visible && image?.video?.fileName;

  const videoInfoAssertion = assertCondition<VideoInfo[]>(
    (val): val is VideoInfo[] =>
      Array.isArray(val) &&
      val.every(
        (item) => isObject(item) && "status" in item && "videoUrl" in item
      )
  );

  $: videoSrc = videoInfoAssertion
    .typeSafeValue(
      getDataSourceValue({
        dataMap: $dataMap ?? {},
        keyString: "video",
      }),
      []
    )
    .find((video) => {
      if (image?.type === "IMAGE" && image.video?.fileName === video.filename) {
        return true;
      }
    });

  onMount(() => {
    pubsub.subscribe("ALL_LOADED", `all-loaded-${node.id}`, () => {
      show = true;
    });

    return () => {
      pubsub.unsubscribe("ALL_LOADED", `all-loaded-${node.id}`);
    };
  });

  let isEndEntranceImage = false;
  let isEndEntranceImageTimeout: ReturnType<typeof setTimeout> | null = null;

  $: if (show) {
    if (isEndEntranceImageTimeout) {
      clearTimeout(isEndEntranceImageTimeout);
      isEndEntranceImageTimeout = null;
    }

    isEndEntranceImageTimeout = setTimeout(() => {
      isEndEntranceImage = true;
    }, 2000);
  }
</script>

<div
  use:detectReaction={{
    node,
    documentNode,
    onAction: reactionEventListener,
  }}
  class:child-shrink-none={!!node.overflow && node.overflow !== "NONE"}
  style={styleToString(
    applyReducer(getFrameStyle(node), (style) => {
      style.padding = 0;
      style.display = "flex";
      style.flexDirection = "row";

      // background로 비디오가 있을 경우, 비디오의 해상도로 aspectRatio를 조정
      if (isBackgroundVideo) {
        if (node.aspectRatio && node.aspectRatio.type === "BACKGROUND_IMAGE") {
          style.aspectRatio = getAspectRatioStyle({
            type: node.aspectRatio.type,
            imageHeight: videoSrc?.height,
            imageWidth: videoSrc?.width,
          });
        }

        style.background = "none";
      }

      if (hideShadow) {
        const { boxShadow: _boxShadow, ...last } = style;
        return last;
      }
      return style;
    })
  )}
>
  {#if isBackgroundVideo}
    <BackgroundVideo fills={node.fills} {videoSrc} />
  {/if}
  <div
    class:entrance-image-frame={show}
    style={styleToString({
      ...getFrameStyle(node),
      position: "relative",
      top: 0,
      right: 0,
      left: 0,
      bottom: 0,
      margin: 0,
      width: "100%",
      height: "auto",
      background: "none",
    })}
  >
    <slot />
  </div>
  {#if !isEndEntranceImage}
    <div
      class:entrance-image={show}
      style={styleToString({
        backgroundImage: `url("${node.entranceImage?.image?.path}")`,
      })}
    />
  {/if}
</div>

<style>
  @keyframes entrance-image {
    0% {
      opacity: 0;
      transform: translateY(20px);
      animation-timing-function: ease-out;
    }

    25%,
    85% {
      opacity: 1;
      transform: translateY(0);
      animation-timing-function: ease-in;
    }

    100% {
      opacity: 0;
    }
  }

  @keyframes entrance-image-frame {
    from {
      opacity: 0;
      transform: translateY(20px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .entrance-image {
    animation: 1.5s 0.5s entrance-image;
    position: absolute;
    inset: 0;
    opacity: 0;
    background-position: center;
    background-repeat: no-repeat;
    background-size: auto;
  }

  .entrance-image-frame {
    animation: 0.5s 2s entrance-image-frame;
    animation-fill-mode: both;
  }

  .child-shrink-none > :global(*) {
    flex-shrink: 0;
  }
</style>
